import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import {
  useSearchStore,
  useSearchAllStores,
} from "../../api/generated/generated";
import type { SearchResult } from "../../api/generated";

interface SearchTestProps {
  storeId?: string;
}

export function SearchTest({ storeId }: SearchTestProps) {
  const [query, setQuery] = useState("");
  const storeSearch = useSearchStore();
  const allSearch = useSearchAllStores();
  const search = storeId ? storeSearch : allSearch;

  const handleSearch = () => {
    if (!query.trim()) return;
    const params = { query: query.trim() };
    if (storeId) {
      storeSearch.mutate({ storeId, data: params });
    } else {
      allSearch.mutate({ data: params });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search documents..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={!query.trim() || search.isPending}
          startIcon={
            search.isPending ? <CircularProgress size={16} /> : <SearchIcon />
          }
        >
          Search
        </Button>
      </Box>
      {search.data?.status === 200 && (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 1
            }}>
            {search.data.data.results.length} results in{" "}
            {search.data.data.query_time_ms.toFixed(0)}ms
          </Typography>

          {search.data.data.results.length === 0 ? (
            <Typography variant="body2" sx={{
              color: "text.disabled"
            }}>
              No matching results found.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {search.data.data.results.map((result: SearchResult) => (
                <Card key={result.chunk_id} variant="outlined">
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mb: 0.5,
                        alignItems: "center",
                      }}
                    >
                      <Chip
                        label={`${(result.score * 100).toFixed(1)}%`}
                        size="small"
                        color={result.score >= 0.8 ? "success" : "default"}
                      />
                      <Typography variant="caption" sx={{
                        color: "text.secondary"
                      }}>
                        {result.filename}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {result.content.length > 500
                        ? result.content.substring(0, 500) + "..."
                        : result.content}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}
      {search.isError && (
        <Typography color="error" variant="body2">
          Search failed. Please try again.
        </Typography>
      )}
    </Box>
  );
}
